/**
 * Local-dev-only override for user role / user level / feature permissions.
 *
 * PURPOSE
 * -------
 * The Console pulls user permissions from two backend responses:
 *   - `GET /user_clients`      -> availableClients.user_role
 *   - `GET /api/console_config`-> pureResponse.user_level, pureResponse.feature_permissions
 *
 * This module lets a developer override those values locally for smoke
 * testing gating logic (DTP button, MAINTENANCE button, admin sidebar
 * items, "rw" feature checks, etc.) WITHOUT touching the backend.
 *
 * USAGE
 * -----
 * Two channels, both are only honored when Vite is in dev mode
 * (`import.meta.env.DEV === true`). In a production build these
 * functions become no-ops and the dead code is stripped by Vite.
 *
 * 1) Env vars (persistent — put in `apps/console_2/.env.local`):
 *      VITE_DEV_USER_ROLE=dtpadmin
 *      VITE_DEV_USER_LEVEL=admin
 *      VITE_DEV_FEATURE_PERMISSIONS={"Promotions":"rw","Banners":"rw"}
 *
 * 2) Runtime toggle (from DevTools console, no restart needed):
 *      window.__DEV_USER_ROLE__ = "webservices_admin";
 *      window.__DEV_SESSION__ = { user_level: "admin", feature_permissions: {} };
 *      // then reload the page or re-select a client
 *
 * The runtime toggle takes precedence over the env var so you can flip
 * roles mid-session.
 *
 * Valid role strings observed in the codebase:
 *   userRole  : "superadmin" | "admin" | "dtpadmin" | "webservices_admin"
 *   userLevel : "superadmin" | "admin"
 *   feature_permissions : { [featureName: string]: "rw" | "" }
 */

/* eslint-disable no-console */

// Guard: `import.meta.env.DEV` is Vite-native and gets DCE'd in prod bundles.
const IS_DEV: boolean = Boolean(import.meta.env && import.meta.env.DEV);

interface DevSessionOverrides {
  user_level?: string;
  feature_permissions?: Record<string, string>;
}

declare global {
  interface Window {
    __DEV_USER_ROLE__?: string;
    __DEV_SESSION__?: DevSessionOverrides;
  }
}

/**
 * Returns the dev override for `userRole`, or `undefined` when disabled.
 */
export const getDevUserRole = (): string | undefined => {
  if (!IS_DEV) return undefined;
  const runtime =
    typeof window !== "undefined" ? window.__DEV_USER_ROLE__ : undefined;
  if (runtime) return runtime;
  const envRole = import.meta.env.VITE_DEV_USER_ROLE as string | undefined;
  return envRole && envRole.length > 0 ? envRole : undefined;
};

/**
 * Returns dev overrides for `sessionDetails` (user_level, feature_permissions),
 * or `undefined` when disabled / nothing set.
 */
export const getDevSessionOverrides = (): DevSessionOverrides | undefined => {
  if (!IS_DEV) return undefined;
  const runtime =
    typeof window !== "undefined" ? window.__DEV_SESSION__ : undefined;

  const envLevel = import.meta.env.VITE_DEV_USER_LEVEL as string | undefined;
  const envPermsRaw = import.meta.env.VITE_DEV_FEATURE_PERMISSIONS as
    | string
    | undefined;

  let envPerms: Record<string, string> | undefined;
  if (envPermsRaw) {
    try {
      envPerms = JSON.parse(envPermsRaw);
    } catch (err) {
      console.warn(
        "[dev-role-override] Failed to parse VITE_DEV_FEATURE_PERMISSIONS as JSON:",
        err
      );
    }
  }

  // Runtime takes precedence field-by-field.
  const merged: DevSessionOverrides = {
    ...(envLevel ? { user_level: envLevel } : {}),
    ...(envPerms ? { feature_permissions: envPerms } : {}),
    ...(runtime || {}),
  };

  return Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * Console-warn helper so overrides are always visible in DevTools.
 */
export const logDevOverride = (
  label: string,
  before: unknown,
  after: unknown
): void => {
  if (!IS_DEV) return;
  console.warn(
    `%c[DEV OVERRIDE] ${label}`,
    "color:#fff;background:#c33;padding:2px 6px;border-radius:3px;",
    { before, after }
  );
  mountDevBadge();
};

// ---------------------------------------------------------------------------
// Optional DEV badge: a small fixed-corner pill so it is obvious an override
// is active. Injected via plain DOM to avoid coupling to the React tree.
// ---------------------------------------------------------------------------
let badgeMounted = false;
const mountDevBadge = (): void => {
  if (!IS_DEV || badgeMounted) return;
  if (typeof document === "undefined" || !document.body) return;
  badgeMounted = true;
  const el = document.createElement("div");
  el.id = "dev-role-override-badge";
  const role = getDevUserRole();
  const session = getDevSessionOverrides();
  const parts: string[] = [];
  if (role) parts.push(`role=${role}`);
  if (session?.user_level) parts.push(`level=${session.user_level}`);
  el.textContent = `DEV OVERRIDE ${parts.join(" ")}`;
  el.style.cssText = [
    "position:fixed",
    "bottom:8px",
    "right:8px",
    "z-index:2147483647",
    "background:#c33",
    "color:#fff",
    "font:12px/1.2 monospace",
    "padding:4px 8px",
    "border-radius:4px",
    "box-shadow:0 2px 6px rgba(0,0,0,.3)",
    "pointer-events:none",
    "user-select:none",
  ].join(";");
  document.body.appendChild(el);
};
